import Axios from "axios";
import { useEffect, useState } from "react";

import qrCodeLive from "../assets/app-download-qr.svg";
import qrCodeLocal from "../assets/app-download-qr-local.svg";
import { configFile } from "../configFile";

export type StorePlatform = "ios" | "android";
export type StoreUrls = Record<StorePlatform, string>;

// Tartalék, ha a szerver nem elérhető vagy a CMS-ben nincs kitöltve a link.
// Az élő értéket a CMS „Alkalmazás verziók" oldala adja (ConfigFiles:
// `ios_store_url`, `android_store_url`) — azt a `/app_version` adja vissza.
export const DEFAULT_STORE_URLS: StoreUrls = {
  ios: "https://apps.apple.com/hu/app/id6784239724",
  android: "https://play.google.com/store/apps/details?id=hu.forno.pizzasprint",
};

// A QR-kód ezt a köztes címet kódolja (lásd `appDownloadQrCode`), az
// `AppDownloadScreen` pedig innen irányít a készülékhez illő store-ba. Így a
// kinyomtatott / képernyőn lévő QR akkor is jó marad, ha a store-link változik.
export const APP_DOWNLOAD_PATH = "/app";

const LIVE_HOSTNAMES = ["www.pizzasprint.hu", "pizzasprint.hu"];

// Két statikus QR (segno-val generálva): élesen `https://www.pizzasprint.hu/app`,
// minden más hoszton (lokális Docker, dev szerver) a helyi `/app` cím.
// Szándékosan az éles hosztokat soroljuk fel, nem a lokálisat: a `build.sh`
// megszakítja a feltöltést, ha a lokális domain szövegként a bundle-be kerül.
export function appDownloadQrCode(): string {
  return LIVE_HOSTNAMES.includes(window.location.hostname)
    ? qrCodeLive
    : qrCodeLocal;
}

// iPadOS 13+ asztali Safari-nak mutatja magát („Macintosh"), csak az
// érintőképernyő árulja el.
export function detectMobilePlatform(): StorePlatform | null {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  return null;
}

// A CMS-ben az Android link `market://details?id=…` alakú, mert az app ezzel
// nyitja meg közvetlenül a Play Áruházat. Böngészőben (főleg asztalon) ez nem
// nyitható meg, a https-es Play link viszont Androidon is az áruház appot hozza.
function toWebStoreUrl(url: string, platform: StorePlatform): string {
  if (url.startsWith("market://details")) {
    return url.replace(
      "market://details",
      "https://play.google.com/store/apps/details"
    );
  }
  return /^https?:\/\//.test(url) ? url : DEFAULT_STORE_URLS[platform];
}

async function fetchStoreUrl(platform: StorePlatform): Promise<string> {
  try {
    // Teljes URL: a komponensek effektjei az App-szintű axios-beállítás előtt
    // is lefuthatnak.
    const res = await Axios.get(`${configFile.serverBaseUrl}/app_version`, {
      params: { platform },
      timeout: 4000,
    });
    const storeUrl: string = res.data?.storeUrl || "";
    return storeUrl
      ? toWebStoreUrl(storeUrl, platform)
      : DEFAULT_STORE_URLS[platform];
  } catch (e) {
    return DEFAULT_STORE_URLS[platform];
  }
}

// Egy oldalbetöltésen belül egyszer kérdezzük le, a komponensek osztoznak rajta.
let storeUrlsPromise: Promise<StoreUrls> | null = null;

export function getStoreUrls(): Promise<StoreUrls> {
  if (!storeUrlsPromise) {
    storeUrlsPromise = Promise.all([
      fetchStoreUrl("ios"),
      fetchStoreUrl("android"),
    ]).then(([ios, android]) => ({ ios, android }));
  }
  return storeUrlsPromise;
}

export function useStoreUrls(): StoreUrls {
  const [urls, setUrls] = useState<StoreUrls>(DEFAULT_STORE_URLS);
  useEffect(() => {
    let isActive = true;
    getStoreUrls().then((result) => {
      if (isActive) setUrls(result);
    });
    return () => {
      isActive = false;
    };
  }, []);
  return urls;
}

// Bezárás után egy ideig nem kérdezünk újra. A localStorage privát módban
// dobhat — ilyenkor egyszerűen megjelenik, a működést nem akasztja meg.
export function isPromoHidden(key: string): boolean {
  try {
    const until = Number(localStorage.getItem(key) || 0);
    return until > Date.now();
  } catch (e) {
    return false;
  }
}

export function hidePromoForDays(key: string, days: number): void {
  try {
    localStorage.setItem(key, String(Date.now() + days * 24 * 60 * 60 * 1000));
  } catch (e) {
    // nincs tárhely — legfeljebb a következő betöltéskor újra megjelenik
  }
}

// A felugró ablak asztalon, a tapadó sáv mobilon jelenik meg — egyszerre
// sosem mindkettő. A határ a Chakra `md` töréspontja (48em).
export function isMobileViewport(): boolean {
  return window.matchMedia("(max-width: 47.99em)").matches;
}
